import type {MouseEventHandler, ReactElement} from 'react';
import React from 'react';
import {AiTextPromptBoxGeneratorOptions} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-generator-options';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {AiTextPromptBoxTextfield} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-textfield';
import {AiButton} from '@Components/ai-button';
import type {
  AiGeneratedContent,
  OneOfPromptOption,
  PromptBoxConfigOptionType,
  RecentlyGeneratedAiContent,
  SelectedValueType,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, GENERATION_OPTIONS_COMPONENT_ID} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import styles from './ai-text-prompt-box-footer.module.scss';
import type {PromiseVoidFunction} from '@/types/global';

interface AiTextPromptBoxFooterProps {
  onSubmitClick: (type: GenerateIdeasOriginator) => Promise<void>;
  onStartOver: MouseEventHandler;
  onDoneClick: VoidFunction;
  onUpdateClick: PromiseVoidFunction;
  isLoadingUsage?: boolean;
  isLoadingAlter?: boolean;
  isLoadingGenerate?: boolean;
  prompt: InputType;
  setPrompt: (value: string) => void;
  areCaptionsLoaded: boolean;
  isHidden: boolean;
  isInMobileMode?: boolean;
  optionsToUse?: PromptBoxConfigOptionType[];
  selectedContent?: AiGeneratedContent | null;
  variant: AiPromptBoxVariant;
  defaultGenerationOptions?: SelectedValueType;
  dropdownInUpdatedState?: PromptBoxConfigOptionType[];
  errorMessage?: string | null;
  placeholder?: string;
  recent?: RecentlyGeneratedAiContent[];
  onGenerationOptionChange?: (changedProperty: OneOfPromptOption) => void;
}

export function AiTextPromptBoxFooter({variant = AiPromptBoxVariant.MONOCONTENT, isLoadingGenerate = false, ...props}: AiTextPromptBoxFooterProps): ReactElement<any> | null {
  if (props.isHidden) {
    return null;
  }

  const isError = !!props.errorMessage;
  const isAlterCaptionIntent = !!props.dropdownInUpdatedState?.length;
  const isErrorInMobile = isError && props.isInMobileMode;

  const onGenerateIdeasClick = (): void => {
    void props.onSubmitClick(GenerateIdeasOriginator.Generate);
  };

  const onStartOverClick = (e: React.MouseEvent): void => {
    props.onStartOver(e);
  };

  const onMoreIdeasClick = (): void => {
    void props.onSubmitClick(GenerateIdeasOriginator.MoreIdeas);
  };

  const getClassesBasedOnVariant = (): string => {
    switch (variant) {
      case AiPromptBoxVariant.MULTICONTENT:
        return 'justify-content-between spacing-p-1';
      case AiPromptBoxVariant.MONOCONTENT:
        return 'justify-content-end';
      default:
        return '';
    }
  };

  const getSelectedContentModeClasses = (): string => {
    if (props.isInMobileMode) return '_full-width spacing-0 flex-v-row';

    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        return '_fit-width spacing-m-2 spacing-m-t-0';
      case AiPromptBoxVariant.MULTICONTENT:
      default:
        return '_full-width spacing-m-2';
    }
  };

  const getMobileSubmitButton = (): ReactElement<any> | null => {
    if (!props.isInMobileMode) {
      return null;
    }

    let buttonIcon;
    let iconPlacement;
    let buttonSize;
    let buttonText;

    switch (variant) {
      case AiPromptBoxVariant.MINIATURE: {
        buttonIcon = 'icon-ai-caption';
        iconPlacement = IconPlacement.LEFT;
        buttonSize = Size.SMALL;
        buttonText = window.i18next.t('pmwjs_update_content');
        break;
      }
      default: {
        buttonIcon = 'icon-chat-send';
        iconPlacement = IconPlacement.RIGHT;
        buttonSize = Size.XSMALL;
        buttonText = window.i18next.t('pmwjs_generate_ideas');
        break;
      }
    }

    return (
      <AiButton
        onClick={onGenerateIdeasClick}
        icon={buttonIcon}
        iconPlacement={iconPlacement}
        size={buttonSize}
        isLoading={isLoadingGenerate}
        text={buttonText}
        customClasses={'spacing-m-t-3'}
        isFullWidth
        disabled={isErrorInMobile ?? props.prompt === ''}
      />
    );
  };

  const getFooterConfigOptionsIfMultiContent = (): ReactElement<any> | null => {
    if (variant !== AiPromptBoxVariant.MULTICONTENT) {
      return null;
    }

    return (
      <AiTextPromptBoxGeneratorOptions
        optionsToUse={props.optionsToUse}
        id={GENERATION_OPTIONS_COMPONENT_ID}
        isDisabled={props.isLoadingAlter || isErrorInMobile}
        isInMobileMode={props.isInMobileMode}
        defaultValues={props.defaultGenerationOptions}
        dropdownInUpdatedState={props.dropdownInUpdatedState}
        isLoading={props.isLoadingAlter}
        darkerDropdown
      />
    );
  };

  const getSelectedContentSubmitButtonClassesBasedOnVariant = (): string => {
    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        return props.isInMobileMode ? 'spacing-m-t-0' : '';
      default:
        return props.isInMobileMode ? 'spacing-m-t-3' : '';
    }
  };

  if (props.selectedContent) {
    return (
      <div className="flexbox _full-width justify-content-end">
        <div
          className={`
            flexbox
            align-items-center
            ${getClassesBasedOnVariant()} 
            ${getSelectedContentModeClasses()} 
          `}
        >
          {getFooterConfigOptionsIfMultiContent()}
          <AiButton
            isFullWidth={props.isInMobileMode}
            size={Size.SMALL}
            isLoading={props.isLoadingUsage || props.isLoadingAlter}
            text={isAlterCaptionIntent ? window.i18next.t('pmwjs_update') : window.i18next.t('pmwjs_done')}
            onClick={(e): void => {
              e.stopPropagation();
              e.preventDefault();
              if (isAlterCaptionIntent) {
                void props.onUpdateClick();
              } else {
                props.onDoneClick();
              }
            }}
            icon={isAlterCaptionIntent ? undefined : 'icon-check'}
            isAiIconHidden={isAlterCaptionIntent}
            customClasses={getSelectedContentSubmitButtonClassesBasedOnVariant()}
          />
        </div>
      </div>
    );
  }

  if (props.areCaptionsLoaded) {
    return (
      <div className={`${styles.aiTextsGenerator} ${props.areCaptionsLoaded ? styles.reduceHeight : ''} ${styles.staticShadow}`}>
        <div className="flexbox flex-row-justify-between">
          <Button
            type={Type.SECONDARY}
            size={Size.XSMALL}
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_change_input')}
            icon="icon-back"
            onClick={onStartOverClick}
          />
          <AiButton
            size={Size.XSMALL}
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_more_ideas')}
            icon="icon-ai"
            onClick={onMoreIdeasClick}
            isLoading={isLoadingGenerate}
          />
        </div>
      </div>
    );
  }

  const getDefaultGenerationOptions = (): SelectedValueType | undefined => {
    if (props.defaultGenerationOptions) {
      return props.defaultGenerationOptions;
    }

    return undefined;
  };

  const getLastCaptionConfigOptions = (): SelectedValueType | undefined => {
    if (!props.recent || !props.recent[0]) {
      return getDefaultGenerationOptions();
    }

    return props.recent[0].configOptions;
  };

  return (
    <div className={`${styles.aiTextsGenerator} ${props.isInMobileMode ? `${styles.inMobileMode} _full-height radius-4` : ''}`}>
      <AiTextPromptBoxTextfield
        isInMobileMode={props.isInMobileMode}
        onSubmitClick={onGenerateIdeasClick}
        value={props.prompt}
        onChange={props.setPrompt}
        isLoading={isLoadingGenerate}
        placeholder={props.placeholder}
        errorMessage={props.errorMessage}
        promptBoxVariant={variant}
      />
      <AiTextPromptBoxGeneratorOptions
        optionsToUse={props.optionsToUse}
        id={GENERATION_OPTIONS_COMPONENT_ID}
        isDisabled={isLoadingGenerate || isErrorInMobile || isError}
        isInMobileMode={props.isInMobileMode}
        darkerDropdown={props.isInMobileMode}
        defaultValues={getLastCaptionConfigOptions()}
        promptBoxVariant={variant}
        onOptionChange={props.onGenerationOptionChange}
      />
      {getMobileSubmitButton()}
    </div>
  );
}
